import { useState } from "react";
import { CalendarCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { getAttribution } from "@/lib/tracking";
import { ensureFacebookPixel, trackFacebookSchedule } from "@/lib/fbPixel";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "" };

// Google Ads + Facebook conversions when a booking request is submitted.
function trackBookingConversion() {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-992700059/tSGXCLaJwc8cEJvNrdkD",
      value: 1.0,
      currency: "EUR",
    });
  }
  if (typeof window !== "undefined" && localStorage.getItem("bh_consent") === "granted") {
    ensureFacebookPixel();
    trackFacebookSchedule();
  }
}

export function BookingDialog({ open, onOpenChange, source = "" }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error(t("err_required"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error(t("err_email_invalid"));
      return;
    }
    setSending(true);
    try {
      const attribution = getAttribution();
      await api.post("/booking", {
        ...form,
        lang,
        source: source || attribution.source || "Bilan (formulaire site)",
      });
      trackBookingConversion();
      toast.success(t("booking_success"));
      setForm(EMPTY);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl text-[#1a2d56] flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-[#d7de3b] text-[#1a2d56] flex items-center justify-center">
              <CalendarCheck size={18} />
            </span>
            {t("booking_title")}
          </DialogTitle>
          <DialogDescription className="text-[#4b5b7c] text-left">{t("booking_subtitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-[#1a2d56] mb-1.5 block">{t("field_first")}</label>
              <Input data-testid="booking-first-name" value={form.first_name} onChange={set("first_name")} placeholder={t("ph_first")} className="rounded-xl" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1a2d56] mb-1.5 block">{t("field_last")} *</label>
              <Input data-testid="booking-last-name" value={form.last_name} onChange={set("last_name")} placeholder={t("ph_last")} className="rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-[#1a2d56] mb-1.5 block">{t("field_phone")}</label>
            <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder={t("ph_phone")} className="rounded-xl" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#1a2d56] mb-1.5 block">{t("booking_email_label")} *</label>
            <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder={t("booking_email_ph")} className="rounded-xl" />
          </div>
          <button
            data-testid="booking-submit"
            onClick={submit}
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1a2d56] text-white font-head font-bold rounded-full px-8 py-3.5 hover:bg-[#111e3a] transition-colors disabled:opacity-60"
          >
            {sending ? t("booking_sending") : t("booking_submit")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
